import { DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ChartComponent } from 'ng-apexcharts';
import {
  CompanyObjectiveKeyResultIngestHelpDialogComponent,
  CompanyObjectiveKeyResultIngestHelpDialogData,
} from 'src/app/components/main/employees/dashboard/company-product-objective/company-objective-key-result-ingest-help-dialog.component';
import { injectCompanyProductFromDetail } from 'src/app/components/main/shared/company-products/company-product-detail/company-product-detail.component';
import {
  CompanyMetricVariableConditionFormDialogComponent,
  CompanyMetricVariableConditionFormDialogData,
} from 'src/app/components/main/shared/company-products/company-product-detail/company-product-objectives/company-metric-variable-condition-form-dialog.component';
import { CompanyMetricColor, CompanyMetricSuffixType } from 'src/app/models/company-metric.model';

@Component({
  selector: 'app-company-metric-card',
  templateUrl: './company-metric-card.component.html',
  styleUrl: './company-metric-card.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DecimalPipe,
    ChartComponent,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
  ],
})
export class CompanyMetricCardComponent {
  private readonly sanitizer = inject(DomSanitizer);
  private readonly dialog = inject(MatDialog);
  private readonly company_product = injectCompanyProductFromDetail();

  readonly metric = input.required<CompanyMetricCardViewModel>();
  readonly metric_updated = output<void>();

  private readonly visualization_index = signal(0);

  readonly active_visualization_index = computed(() => {
    const length = this.metric().charts.length;
    if (length === 0) {
      return 0;
    }

    return this.visualization_index() % length;
  });

  readonly active_chart = computed(() => {
    const charts = this.metric().charts;
    if (charts.length === 0) {
      return null;
    }

    return charts[this.active_visualization_index()] ?? null;
  });

  readonly has_carousel = computed(() => this.metric().charts.length > 1);

  readonly incremental_value_number = computed(() => this.toNumberOrNull(this.metric().simple_type_incremental_value));

  readonly show_month_trend = computed(() => {
    const metric = this.metric();
    return metric.has_month_trend && metric.month_trend_percent !== null;
  });

  readonly month_trend_arrow = computed(() => {
    const percent = this.metric().month_trend_percent;
    if (percent === null) {
      return 'trending_flat';
    }
    if (percent > 0) {
      return 'trending_up';
    }
    if (percent < 0) {
      return 'trending_down';
    }

    return 'trending_flat';
  });

  readonly month_trend_color_class = computed(() => {
    switch (this.metric().month_trend_semantic) {
      case 'good':
        return 'text-bisual-green';
      case 'bad':
        return 'text-bisual-red';
      default:
        return 'text-bisual-gray';
    }
  });

  readonly status_badge_class = computed(() => {
    switch (this.metric().simple_type_value_color) {
      case 'green':
        return 'metric-status-badge-good';
      case 'red':
        return 'metric-status-badge-bad';
      case 'orange':
        return 'metric-status-badge-mid';
      default:
        return 'metric-status-badge-neutral';
    }
  });

  readonly value_color_class = computed(() => this.colorClass(this.metric().simple_type_value_color));
  readonly incremental_color_class = computed(() => this.colorClass(this.metric().simple_type_incremental_color));
  readonly circular_progress_color_class = computed(() =>
    this.progressColorClass(this.metric().simple_type_suffix_circular_progress_color)
  );
  readonly progress_bar_color_class = computed(() => this.progressBarColorClass(this.metric().simple_type_suffix_progress_bar_color));

  readonly sanitized_svg_line = computed(() => {
    const html = this.metric().simple_type_suffix_svg_line_html;
    if (!html) {
      return '';
    }

    return this.sanitizer.bypassSecurityTrustHtml(html);
  });

  readonly can_edit_thresholds = computed(
    () => this.metric().can_edit_variable_condition && this.metric().company_metric_variable_condition_id !== null
  );

  constructor() {
    effect(() => {
      this.metric().id;
      this.visualization_index.set(0);
    });
  }

  showPreviousVisualization(): void {
    const length = this.metric().charts.length;
    if (length < 2) {
      return;
    }

    this.visualization_index.update(index => (index - 1 + length) % length);
  }

  showNextVisualization(): void {
    const length = this.metric().charts.length;
    if (length < 2) {
      return;
    }

    this.visualization_index.update(index => (index + 1) % length);
  }

  openIngestHelp(): void {
    const metric = this.metric();
    const company_product = this.company_product();
    if (company_product === null) {
      return;
    }

    const data: CompanyObjectiveKeyResultIngestHelpDialogData = {
      key_result_title: metric.title,
      company_metric_slug: metric.slug,
      company_metric_title: metric.title,
      company_metric_relatable: metric.company_metric_relatable,
      company_product_id: company_product.id,
      unit: metric.simple_type_value_units,
    };

    this.dialog.open(CompanyObjectiveKeyResultIngestHelpDialogComponent, {
      width: '640px',
      maxWidth: '95vw',
      data,
    });
  }

  openVariableConditionDialog(): void {
    const metric = this.metric();
    const variable_condition_id = metric.company_metric_variable_condition_id;
    if (variable_condition_id === null) {
      return;
    }

    const data: CompanyMetricVariableConditionFormDialogData = {
      company_metric_variable_condition_id: variable_condition_id,
      key_result_title: metric.title,
      acceptable_value: metric.acceptable_value,
      good_value: metric.good_value,
      evaluation: metric.evaluation,
      unit: metric.unit,
      can_edit_variable_condition: metric.can_edit_variable_condition,
      fork_on_save: true,
    };

    this.dialog
      .open(CompanyMetricVariableConditionFormDialogComponent, {
        width: '640px',
        maxWidth: '95vw',
        data,
      })
      .afterClosed()
      .subscribe((saved: boolean | undefined) => {
        if (saved) {
          this.metric_updated.emit();
        }
      });
  }

  private colorClass(color: string | null | undefined): string {
    switch (color) {
      case 'green':
        return 'text-bisual-green';
      case 'red':
        return 'text-bisual-red';
      case 'blue':
        return 'text-bisual-blue';
      case 'orange':
      case 'yellow':
        return 'text-bisual-yellow';
      case 'gray':
        return 'text-bisual-gray';
      case 'black':
      default:
        return 'text-bisual-black';
    }
  }

  private progressColorClass(color: string | null | undefined): string {
    switch (color) {
      case 'green':
        return 'progress-spinner-green';
      case 'red':
        return 'progress-spinner-red';
      case 'blue':
        return 'progress-spinner-blue';
      case 'orange':
        return 'progress-spinner-orange';
      case 'yellow':
        return 'progress-spinner-yellow';
      case 'black':
        return 'progress-spinner-black';
      default:
        return 'progress-spinner-blue';
    }
  }

  private progressBarColorClass(color: string | null | undefined): string {
    switch (color) {
      case 'green':
        return 'progress-bar-green';
      case 'red':
        return 'progress-bar-red';
      case 'blue':
        return 'progress-bar-blue';
      case 'orange':
        return 'progress-bar-orange';
      case 'yellow':
        return 'progress-bar-yellow';
      case 'black':
        return 'progress-bar-black';
      default:
        return 'progress-bar-blue';
    }
  }

  private toNumberOrNull(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value === 'number') {
      return value;
    }
    const parsed = parseFloat(value);

    return Number.isNaN(parsed) ? null : parsed;
  }
}

export interface CompanyMetricCardViewModel {
  id: number;
  title: string;
  slug: string;
  value: number | null;
  created_at: string | null;
  visualization_order: number;
  bootstrap_class: string;
  charts: Record<string, unknown>[];
  company_metric_relatable: 'company' | 'product';
  simple_type_value: string;
  simple_type_value_units: string | null;
  simple_type_value_color: CompanyMetricColor | null;
  simple_type_incremental_prcnt: string | number | null;
  simple_type_incremental_value: string | number | null;
  simple_type_incremental_color: CompanyMetricColor | null;
  simple_type_footer_text: string | null;
  simple_type_suffix_type: CompanyMetricSuffixType | null;
  simple_type_suffix_icon_google_name: string | null;
  simple_type_suffix_progress_bar_prcnt: string | number | null;
  simple_type_suffix_progress_bar_color: CompanyMetricColor | null;
  simple_type_suffix_circular_progress_prcnt: string | number | null;
  simple_type_suffix_circular_progress_color: CompanyMetricColor | null;
  simple_type_suffix_svg_line_html: string | null;
  company_metric_variable_condition_id: number | null;
  acceptable_value: number | null;
  good_value: number | null;
  evaluation: 'higher_is_better' | 'lower_is_better' | 'exact' | null;
  unit: string | null;
  can_edit_variable_condition: boolean;
  has_month_trend: boolean;
  month_trend_percent: number | null;
  month_trend_semantic: 'good' | 'bad' | 'neutral';
  status_label: string;
}
